s<template>
  <div class="card h-100" :class="{ 'setup-mode': isSetupMode }">
    <div class="card-header">
      <div class="header-icon">
        <i :class="isSetupMode ? 'bi bi-gear-fill' : 'bi bi-receipt'"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">{{ isSetupMode ? 'Setup Your Monthly Budget' : 'Monthly Expenses' }}</h3>
        <p class="card-subtitle">{{ isSetupMode ? 'Configure your income and expenses' : 'Manage your recurring expenses' }}</p>
      </div>
    </div>

    <div class="card-body">
      <!-- Monthly Income Section (only in setup mode) -->
      <div v-if="isSetupMode" class="income-section nested-container mb-4">
        <label for="grossMonthlyIncome" class="form-label fw-semibold">
          <i class="bi bi-cash-stack me-1"></i>Monthly Income (Gross)
        </label>
        <div class="input-group input-group-lg">
          <span class="input-group-text currency-symbol">$</span>
          <input 
            type="number" 
            class="form-control" 
            id="grossMonthlyIncome"
            :value="grossIncome"
            @input="$emit('update:grossIncome', parseFloat($event.target.value) || 0)"
            placeholder="Enter your gross monthly income"
            step="0.01">
        </div>
      </div>

      <!-- Expenses Section Header -->
      <div class="expenses-header mb-3">
        <label class="form-label fw-semibold mb-0">
          <i class="bi bi-receipt me-1"></i>{{ isSetupMode || isReadOnly ? 'Monthly Expenses' : 'Add Expense' }}
        </label>
        <div v-if="isReadOnly && !isSetupMode" class="read-only-badge">
          <i class="bi bi-lock-fill me-1"></i>Read Only
        </div>
      </div>

      <!-- Add Expense Form -->
      <div v-if="!isReadOnly" class="add-expense-section nested-container mb-3">
        <div class="row g-3">
          <div class="col-12 col-md-7">
            <label for="expenseName" class="form-label fw-semibold">
              <i class="bi bi-card-text me-1"></i>Expense Name
            </label>
            <input 
              type="text" 
              class="form-control form-control-lg" 
              id="expenseName"
              v-model="newExpense.name"
              placeholder="e.g., Rent, Utilities"
              @keydown.enter="addExpense">
          </div>
          <div class="col-12 col-md-5">
            <label for="expenseAmount" class="form-label fw-semibold">
              <i class="bi bi-currency-dollar me-1"></i>Amount
            </label>
            <div class="input-group input-group-lg">
              <span class="input-group-text currency-symbol">$</span>
              <input 
                type="number" 
                class="form-control" 
                id="expenseAmount"
                v-model.number="newExpense.amount" 
                placeholder="0.00"
                step="0.01"
                @keydown.enter="addExpense">
            </div>
          </div>
        </div>
        <button 
          @click="addExpense" 
          class="btn btn-primary btn-lg w-100 mt-3"
          :disabled="!newExpense.name || !newExpense.amount">
          <i class="bi bi-plus-lg me-1"></i>Add Expense
        </button>
      </div>

      <!-- Expenses List -->
      <div v-if="expenses.length > 0" class="expenses-list">
        <div 
          v-for="(expense, index) in expenses" 
          :key="index"
          class="expense-item nested-container">
          <div class="expense-info">
            <span class="expense-name">{{ expense.name }}</span>
            <span class="expense-amount">${{ expense.amount.toFixed(2) }}</span>
          </div>
          <button 
            v-if="!isReadOnly"
            @click="confirmDelete(expense, index)" 
            class="btn delete-btn">
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="bi bi-inbox"></i>
        <p>No expenses added yet</p>
      </div>

      <!-- Total Summary -->
      <div v-if="expenses.length > 0" class="total-summary">
        <div class="summary-row nested-container">
          <span class="summary-label">Total Monthly Expenses</span>
          <span class="summary-value">${{ totalExpenses.toFixed(2) }}</span>
        </div>
      </div>

      <!-- Net Income Display (only in setup mode) -->
      <div v-if="isSetupMode" class="net-income-section mt-4">
        <div class="net-income-card nested-container">
          <div class="income-row">
            <span class="income-label">Gross Income:</span>
            <span class="income-value">${{ (grossIncome || 0).toFixed(2) }}</span>
          </div>
          <div class="income-row">
            <span class="income-label">Total Expenses:</span>
            <span class="income-value expense">-${{ totalExpenses.toFixed(2) }}</span>
          </div>
          <div class="divider"></div>
          <div class="income-row net">
            <span class="income-label">Net Monthly Income:</span>
            <span class="income-value" :class="netIncome > 0 ? 'positive' : 'negative'">
              ${{ netIncome.toFixed(2) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Setup Button (only in setup mode) -->
      <button 
        v-if="isSetupMode"
        @click="completeSetup" 
        class="btn btn-primary btn-lg w-100 mt-4"
        :disabled="netIncome <= 0">
        <i class="bi bi-check-lg me-2"></i>Complete Setup
      </button>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Delete Expense</h4>
          <button type="button" class="btn-close" @click="cancelDelete" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ expenseToDelete?.name }}</strong>?</p>
          <p class="text-muted">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn btn-outline-secondary">Cancel</button>
          <button @click="confirmRemoveExpense" class="btn btn-danger">
            <i class="bi bi-trash-fill me-1"></i>Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { storeBudget, deleteBudgetCategory } from '../utils/budgetService.ts';
import { useAuth } from '../composables/useAuth';
import { useErrorModal } from '../composables/useErrorModal';

export default {
  name: 'MonthlyExpensesBreakdown',
  props: {
    expenses: {
      type: Array,
      required: true
    },
    grossIncome: {
      type: Number,
      default: 0
    },
    isSetupMode: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { user, getAccessToken } = useAuth();
    const { showApiError } = useErrorModal();
    return { user, getAccessToken, showApiError };
  },
  data() {
    return {
      newExpense: {
        name: '',
        amount: null
      },
      showDeleteConfirm: false,
      expenseToDelete: null,
      deleteIndex: null
    };
  },
  computed: {
    totalExpenses() {
      return this.expenses.reduce((sum, expense) => sum + expense.amount, 0);
    },
    netIncome() {
      return Math.max(0, (this.grossIncome || 0) - this.totalExpenses);
    }
  },
  methods: {
    async addExpense() {
      if (this.newExpense.name && this.newExpense.amount) {
        this.$emit('add-expense', {
          name: this.newExpense.name,
          amount: parseFloat(this.newExpense.amount)
        });
        
        // Save to API if user is authenticated and not in setup mode
        if (this.user && !this.isSetupMode) {
          await this.saveBudgetToAPI();
        }
        
        this.newExpense = {
          name: '',
          amount: null
        };
      }
    },
    confirmDelete(expense, index) {
      this.expenseToDelete = expense;
      this.deleteIndex = index;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.expenseToDelete = null;
      this.deleteIndex = null;
    },
    async confirmRemoveExpense() {
      if (this.expenseToDelete && this.deleteIndex !== null) {
        // Delete from API first if user is authenticated and not in setup mode
        if (this.user && !this.isSetupMode) {
          try {
            const accessToken = await this.getAccessToken();
            await deleteBudgetCategory(this.expenseToDelete.name, accessToken);
            console.log('Budget category deleted from API:', this.expenseToDelete.name);
          } catch (error) {
            console.error('Failed to delete budget category from API:', error);
            this.showApiError(error);
            // Continue with local deletion even if API fails
          }
        }
        
        // Emit to parent to update local state
        this.$emit('remove-expense', this.deleteIndex);
        
        // Close modal
        this.cancelDelete();
      }
    },
    async removeExpense(index) {
      // This method is now replaced by the confirmation flow
      this.confirmDelete(this.expenses[index], index);
    },
    async saveBudgetToAPI() {
      try {
        const accessToken = await this.getAccessToken();
        
        // Transform current budget data to API format
        const budgetItems = [];
        
        // Add income items
        if (this.grossIncome > 0) {
          budgetItems.push({
            name: 'salary',
            amount: this.grossIncome,
            kind: 'income',
            currency: 'USD'
          });
        }
        
        // Add expense items
        this.expenses.forEach(expense => {
          budgetItems.push({
            name: expense.name,
            amount: expense.amount,
            kind: 'expense',
            currency: 'USD'
          });
        });
        
        if (budgetItems.length > 0) {
          const result = await storeBudget(budgetItems, accessToken);
          console.log('Budget saved to API:', result.data.summary);
        }
      } catch (error) {
        console.error('Failed to save budget to API:', error);
        this.showApiError(error);
      }
    },
    completeSetup() {
      if (this.netIncome > 0) {
        this.showConfetti();
        setTimeout(() => {
          this.$emit('complete-setup');
        }, 300);
      }
    },
    showConfetti() {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '9999';
      document.body.appendChild(canvas);

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const ctx = canvas.getContext('2d');

      const confetti = [];
      const colors = ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
      const confettiCount = 150;

      for (let i = 0; i < confettiCount; i++) {
        confetti.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          r: Math.random() * 6 + 4,
          d: Math.random() * confettiCount,
          color: colors[Math.floor(Math.random() * colors.length)],
          tilt: Math.floor(Math.random() * 10) - 10,
          tiltAngleIncremental: Math.random() * 0.07 + 0.05,
          tiltAngle: 0
        });
      }

      let animationFrame;
      let startTime = Date.now();
      const duration = 4000;
      const fadeStart = 2000;

      const draw = () => {
        const elapsed = Date.now() - startTime;
        const opacity = elapsed > fadeStart ? 1 - (elapsed - fadeStart) / (duration - fadeStart) : 1;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = opacity;

        confetti.forEach((c, i) => {
          ctx.beginPath();
          ctx.lineWidth = c.r / 2;
          ctx.strokeStyle = c.color;
          ctx.moveTo(c.x + c.tilt + c.r / 4, c.y);
          ctx.lineTo(c.x + c.tilt, c.y + c.tilt + c.r / 4);
          ctx.stroke();

          c.tiltAngle += c.tiltAngleIncremental;
          c.y += (Math.cos(c.d) + 3 + c.r / 2) / 2;
          c.x += Math.sin(c.d);
          c.tilt = Math.sin(c.tiltAngle - i / 3) * 15;

          if (c.y > canvas.height) {
            confetti[i] = {
              x: Math.random() * canvas.width,
              y: -10,
              r: c.r,
              d: c.d,
              color: c.color,
              tilt: c.tilt,
              tiltAngleIncremental: c.tiltAngleIncremental,
              tiltAngle: c.tiltAngle
            };
          }
        });

        if (elapsed < duration) {
          animationFrame = requestAnimationFrame(draw);
        } else {
          document.body.removeChild(canvas);
        }
      };

      draw();
    }
  }
};
</script>

<style scoped>
.card {
  background-color: var(--bg-card);
  border: none;
  border-radius: 5px;
  border-top: 7px solid var(--light-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card.setup-mode {
  max-width: 700px;
  margin: 0 auto;
}

.card-header {
  background: none;
  border: none;
  padding: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 1rem;
  border-bottom: 1px solid var(--light-border);
}

.header-content {
  flex: 1;
  text-align: left;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0;
  line-height: 1.2;
}

.card-subtitle {
  font-size: 0.85rem;
  color: var(--light-text-muted);
  margin: 0.25rem 0 0;
  line-height: 1.2;
}

.card-body {
  padding: 0 1rem 1rem 1rem;
}

.income-section {
  /* Uses shared .nested-container class for styling */
}

.expenses-header {
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.read-only-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: var(--light-border);
  color: var(--light-text-muted);
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.read-only-badge i {
  font-size: 0.75rem;
}

.add-expense-section {
  /* Uses shared .nested-container class for styling */
}

.expenses-list {
  margin-bottom: 1rem;
}

.expense-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  /* Uses shared .nested-container class for styling */
}

.expense-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

:global([data-theme]) .expense-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.expense-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.expense-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.expense-amount {
  font-weight: 700;
  color: var(--expense-color);
  font-size: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}



.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 3rem;
  display: block;
  margin-bottom: 0.5rem;
  color: var(--light-text);
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.total-summary {
  padding-top: 1rem;
  border-top: 2px solid var(--border-color);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Uses shared .nested-container class for styling */
}

.summary-label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.summary-value {
  font-weight: 700;
  color: var(--expense-color);
  font-size: 1.25rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Form Controls */
.form-label {
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.form-label i {
  color: var(--light-text);
}

.form-control {
  border-radius: 0.5rem;
  border: 2px solid var(--border-color);
  transition: all 0.2s ease;
  background-color: var(--bg-card);
  color: var(--text-primary);
}

.form-control:focus {
  border-color: var(--light-primary);
  box-shadow: 0 0 0 0.2rem rgba(19, 100, 163, 0.25);
  background-color: var(--bg-card);
  color: var(--text-primary);
}

.form-control::placeholder {
  color: var(--text-secondary);
  opacity: 0.6;
}

.input-group-text {
  border-radius: 0.5rem 0 0 0.5rem;
  background-color: var(--bg-secondary);
  border: 2px solid var(--border-color);
  color: var(--text-primary);
  font-weight: 600;
}

.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 44px;
}

.btn-primary:hover {
  background-color: #2a8fd4;
  border-color: #2a8fd4;
}

/* Net Income Section */
.net-income-section {
  padding: 0;
}

.net-income-card {
  /* Uses shared .nested-container class for styling */
}

.income-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.income-row.net {
  padding-top: 0.75rem;
}

.income-label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.income-row.net .income-label {
  font-weight: 700;
  font-size: 1rem;
}

.income-value {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.income-value.expense {
  color: var(--expense-color);
}

.income-value.positive {
  color: var(--light-success);
  font-weight: 700;
  font-size: 1.25rem;
}

.income-value.negative {
  color: var(--expense-color);
  font-weight: 700;
  font-size: 1.25rem;
}

.income-row.net .income-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.divider {
  height: 2px;
  background: var(--light-border);
  margin: 0.5rem 0;
}

/* Scrollbar Styling */
.expenses-list::-webkit-scrollbar {
  width: 6px;
}

.expenses-list::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 3px;
}

.expenses-list::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.expenses-list::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .card-title {
    font-size: 1rem;
  }

  .card-subtitle {
    font-size: 0.8rem;
  }

  .expense-name {
    font-size: 0.875rem;
  }

  .expense-amount {
    font-size: 0.95rem;
  }

  .summary-value {
    font-size: 1.1rem;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: var(--bg-card);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--light-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--light-border);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

.modal-body p {
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.modal-body .text-muted {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--light-border);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.btn-danger {
  background-color: var(--expense-color);
  border-color: var(--expense-color);
  color: white;
  min-height: 44px;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-outline-secondary {
  min-height: 44px;
}
</style>
